// Portfolio Administration v1.0 PR 6 — versioned editor for a single
// waterfall code. Replaces the PR 0 ComingSoonPage at
// /portfolio-admin/waterfalls/:code. Behind PortfolioAdminV1.

import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ChevronLeft } from "lucide-react";
import { Button, Card } from "@/design-system/components/ui";
import { useAuth } from "@/hooks/useAuth";
import { useFeatureFlag } from "@/hooks/portfolioAdmin/useFeatureFlag";
import {
  useCreateWaterfallDraft,
  useUpdateWaterfallDraft,
  useWaterfallHistory,
} from "@/hooks/portfolioAdmin/usePr6";
import { ComingSoonPage } from "@/components/ComingSoonPage";
import { PriorityDetailDrawer } from "@/components/portfolioAdmin/waterfalls/PriorityDetailDrawer";
import { PriorityRowsEditor } from "@/components/portfolioAdmin/waterfalls/PriorityRowsEditor";
import { ProposeApproveRejectButtons } from "@/components/portfolioAdmin/waterfalls/ProposeApproveRejectButtons";
import { RunHistorySidebar } from "@/components/portfolioAdmin/waterfalls/RunHistorySidebar";
import { VersionDiff } from "@/components/portfolioAdmin/waterfalls/VersionDiff";
import { VersionTimeline } from "@/components/portfolioAdmin/waterfalls/VersionTimeline";
import { WaterfallStatusBadge } from "@/components/portfolioAdmin/waterfalls/WaterfallStatusBadge";
import type {
  PriorityRow,
  WaterfallConfigDto,
} from "@/api/portfolioAdmin/pr6Types";

// Payment System v3.0 PR 3c (design spec §6.4): canonical unified model is
// WF1+WF2. WF3-WF6 are retained only as a display fallback so the detail
// page of a historical legacy-coded run still resolves a human name.
const WATERFALL_NAMES: Record<string, string> = {
  WF1: "Retained-share waterfall",
  WF2: "Contributed-share waterfall",
};

const LEGACY_WATERFALL_NAMES: Record<string, string> = {
  WF3: "SPV Vendor Income",
  WF4: "PMVFI Recovery",
  WF5: "Closing Proceeds",
  WF6: "Prepayment Event",
};

function waterfallName(code: string): string {
  return WATERFALL_NAMES[code] ?? LEGACY_WATERFALL_NAMES[code] ?? code;
}

function parsePriorities(json: string): PriorityRow[] {
  try {
    const parsed = JSON.parse(json);
    if (Array.isArray(parsed)) return parsed as PriorityRow[];
    return [];
  } catch {
    return [];
  }
}

function fmtDate(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toISOString().slice(0, 10);
}

export default function WaterfallDetailPage() {
  const flagOn = useFeatureFlag("PortfolioAdminV1");
  const params = useParams<{ code?: string }>();
  const code = params.code ?? "";
  if (!flagOn) {
    return (
      <ComingSoonPage
        feature="Waterfalls"
        pr="PR 6"
        description="Admin-only versioned editor for the unified waterfall configurations (WF1 retained-share, WF2 contributed-share)."
        backTo="/portfolio-admin/waterfalls"
        backLabel="Back to Waterfalls"
      />
    );
  }
  return <WaterfallDetailPageInner code={code} />;
}

interface WaterfallDetailPageInnerProps {
  code: string;
}

function WaterfallDetailPageInner({ code }: WaterfallDetailPageInnerProps) {
  const auth = useAuth();
  const canEdit =
    auth.hasPermission("Waterfall.ConfigEdit") || auth.hasRoleCode("ADMIN");

  const historyQuery = useWaterfallHistory(code);
  const versions = useMemo(
    () => historyQuery.data ?? [],
    [historyQuery.data],
  );

  const active = useMemo(
    () => versions.find((v) => v.status === "active") ?? null,
    [versions],
  );
  const initialSelected = useMemo(
    () =>
      versions.find((v) => v.status === "proposed")?.id ??
      versions.find((v) => v.status === "draft")?.id ??
      active?.id ??
      versions[0]?.id ??
      null,
    [versions, active],
  );

  const [selectedId, setSelectedId] = useState<string | null>(null);
  useEffect(() => {
    if (!selectedId && initialSelected) setSelectedId(initialSelected);
  }, [initialSelected, selectedId]);

  const selected: WaterfallConfigDto | null = useMemo(
    () => versions.find((v) => v.id === selectedId) ?? null,
    [versions, selectedId],
  );

  const [draftRows, setDraftRows] = useState<PriorityRow[] | null>(null);
  // Drill-down drawer target (a priority position on the SELECTED version).
  const [openDetailPosition, setOpenDetailPosition] = useState<number | null>(
    null,
  );
  // Reset the draft buffer + any open drill-down whenever the selected
  // version changes.
  useEffect(() => {
    setDraftRows(null);
    setOpenDetailPosition(null);
  }, [selectedId]);

  const editable = !!selected && selected.status === "draft" && canEdit;
  const rows: PriorityRow[] = useMemo(() => {
    if (editable && draftRows) return draftRows;
    return selected ? parsePriorities(selected.prioritiesJson) : [];
  }, [editable, draftRows, selected]);

  const updateMutation = useUpdateWaterfallDraft(code);
  const createDraft = useCreateWaterfallDraft(code);

  const dirty = editable && draftRows !== null;

  const handleSave = () => {
    if (!selected || !draftRows) return;
    updateMutation.mutate(
      {
        configId: selected.id,
        body: {
          prioritiesJson: JSON.stringify(draftRows),
        },
      },
      { onSuccess: () => setDraftRows(null) },
    );
  };

  const handleProposeChanges = () => {
    if (!active) return;
    createDraft.mutate(
      {
        prioritiesJson: active.prioritiesJson,
        algorithmRef: active.algorithmRef,
        changeJustification: "PR 6 — propose changes",
      },
      {
        onSuccess: (draft) => setSelectedId(draft.id),
      },
    );
  };

  const baseForDiff: WaterfallConfigDto | null =
    selected && active && selected.id !== active.id ? active : null;

  return (
    <div
      className="flex flex-col gap-space-3"
      data-testid="waterfall-detail-page"
    >
      <Link
        to="/portfolio-admin/waterfalls"
        className="inline-flex items-center gap-space-1 text-small text-gold-shadow hover:underline"
        data-testid="waterfall-detail-back"
      >
        <ChevronLeft size={14} aria-hidden="true" /> Back to waterfalls
      </Link>

      <div className="flex items-start justify-between gap-space-2 flex-wrap">
        <div>
          <h1 className="font-display font-bold text-h1 m-0">
            {code} <span className="text-h3 text-gray-mid font-normal">— {waterfallName(code)}</span>
          </h1>
          {selected && (
            <p className="text-small text-gray-mid m-0">
              Currently viewing: v{selected.version} ·{" "}
              <WaterfallStatusBadge status={selected.status} />
              {selected.status === "active" && (
                <span className="ml-space-1">since {fmtDate(selected.effectiveStartDate)}</span>
              )}
            </p>
          )}
        </div>
        <div className="flex items-center gap-space-2">
          {active && (!selected || selected.id === active.id) && canEdit && (
            <Button
              type="button"
              variant="secondary"
              size="sm"
              disabled={createDraft.isPending}
              onClick={handleProposeChanges}
              data-testid="propose-changes-button"
            >
              {createDraft.isPending ? "Creating draft…" : "Propose changes"}
            </Button>
          )}
          {selected && (
            <ProposeApproveRejectButtons
              waterfallCode={code}
              config={selected}
            />
          )}
        </div>
      </div>

      <Card className="flex flex-col gap-space-2">
        <h2 className="font-display font-bold text-h3 m-0">Version timeline</h2>
        {historyQuery.isLoading ? (
          <p className="text-small text-gray-mid m-0">Loading versions…</p>
        ) : (
          <VersionTimeline
            versions={versions}
            selectedConfigId={selectedId}
            onSelect={setSelectedId}
          />
        )}
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-[2fr_1fr] gap-space-3">
        <div className="flex flex-col gap-space-3">
          {selected ? (
            <Card className="flex flex-col gap-space-2">
              <div className="flex items-center justify-between flex-wrap gap-space-2">
                <h2 className="font-display font-bold text-h3 m-0">
                  Priorities · v{selected.version}
                </h2>
                {editable && (
                  <Button
                    type="button"
                    variant="primary"
                    size="sm"
                    disabled={!dirty || updateMutation.isPending}
                    onClick={handleSave}
                    data-testid="save-draft-button"
                  >
                    {updateMutation.isPending ? "Saving…" : "Save draft"}
                  </Button>
                )}
              </div>
              {selected.status === "active" && (
                <p className="text-caption text-gray-mid m-0">
                  Active versions are immutable. Click "Propose changes" above
                  to create an editable draft.
                </p>
              )}
              <PriorityRowsEditor
                rows={rows}
                editable={editable}
                onChange={(next) => setDraftRows(next)}
                onShowDetail={setOpenDetailPosition}
              />
            </Card>
          ) : (
            <p className="text-small text-gray-mid italic">
              Select a version above to view its priorities.
            </p>
          )}

          {baseForDiff && selected && (
            <VersionDiff
              baseRows={parsePriorities(baseForDiff.prioritiesJson)}
              proposedRows={rows}
            />
          )}
        </div>

        <div className="flex flex-col gap-space-3">
          <RunHistorySidebar waterfallCode={code} />
        </div>
      </div>

      {selected && openDetailPosition !== null && (
        <PriorityDetailDrawer
          configId={selected.id}
          position={openDetailPosition}
          hasUnsavedEdits={dirty}
          onClose={() => setOpenDetailPosition(null)}
        />
      )}
    </div>
  );
}
